<script setup>
import { usePageHead } from '@composables/use-page-head'
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAppearanceStore } from '@state/stores/appearance'
import { useMemberStore } from '@state/stores/member'
import { usePremiumStore } from '@state/stores/premium'
import { useMessages } from '@composables/use-messages'
import DataLayer from '@services/data-layer/index'
import modalService from '@services/modal-service'
import formatDate from './../../../utils/format-date-full'
import { feedbackMessageMemberId } from '../../../configuration'

const appearanceStore = useAppearanceStore()
const memberStore = useMemberStore()
const premiumStore = usePremiumStore()
const router = useRouter()
const getMessageText = useMessages()

const selectedChoice = ref('')
const textFeedback = ref('')
const isLoading = ref(false)

const crumbs = [
    { label: 'Account Management', to: '/account' },
    { label: 'Cancel Premium' },
    { label: 'Cancellation feedback' },
]

const currentSubscription = computed(() => premiumStore.currentSubscription)

const submitFeedback = async () => {
    if (!selectedChoice.value || (selectedChoice.value === 'other' && !textFeedback.value)) {
        return appearanceStore.handleNotification({
            message: getMessageText('MISSING_REQUIRED_FIELDS'),
            type: 'warning',
        })
    }

    const message = `Reason: ${selectedChoice.value} \n ${selectedChoice.value === 'other' ? 'More details:' + textFeedback.value : ''}`

    isLoading.value = true

    try {
        await DataLayer.messages.sendMessage({
            message: message.replace(/\n/g, '<br>'),
            subject: 'Cancellation feedback',
            content: "FRISKY Premium cancellation feedback",
            recipientId: feedbackMessageMemberId,
            senderId: memberStore.currentMember.id,
        })
        isLoading.value = false
        modalService.show('CANCELLATION_FEEDBACK_SUCCESS', {
            onClose: () => {
                router.push('/account')
            },
        })
    } catch (_error) {
        isLoading.value = false
        modalService.show('CANCELLATION_FEEDBACK_ERROR')
    }
}

usePageHead({ title: 'Cancel Premium feedback', description: 'Cancel Premium feedback' })
</script>

<template>
    <div>
        <BaseBreadcrumbs :crumbs="crumbs"/>
        <div class="cancellation-feedback-container">
            <h1 class="title">Premium cancelled. :(</h1>
            <div class="subtitles">
                <p class="subtitle text-white">Your subscription ends on {{ formatDate(new Date(currentSubscription.endTime)) }}.</p>
                <p class="subtitle text-white">To help us improve Premium, please tell us why you cancelled:</p>
            </div>
            <form @submit.prevent="submitFeedback">
                <h3 class="feedback-header text-white fw-bold">Please tell us why you cancelled Premium?</h3>
                <div class="choices">
                    <div class="radio-group">
                        <input
                            id="switch-plan"
                            v-model="selectedChoice"
                            type="radio"
                            class="radio-input"
                            name="switch-plan"
                            value="I am switching to a different plan"
                        >
                        <label
                            class="radio-label"
                            for="switch-plan"
                        >I am switching to a different plan</label>
                    </div>
                    <div class="radio-group">
                        <input
                            id="cant-afford"
                            v-model="selectedChoice"
                            type="radio"
                            class="radio-input"
                            name="cant-afford"
                            value="I cant afford Premium anymore"
                        >
                        <label
                            class="radio-label"
                            for="cant-afford"
                        >I cant afford Premium anymore</label>
                    </div>
                    <div class="radio-group">
                        <input
                            id="cant-find-music"
                            v-model="selectedChoice"
                            type="radio"
                            class="radio-input"
                            name="cant-find-music"
                            value="I cant find music that I like"
                        >
                        <label
                            class="radio-label"
                            for="cant-find-music"
                        >I cant find music that I like</label>
                    </div>
                    <div class="radio-group">
                        <input
                            id="technical-issues"
                            v-model="selectedChoice"
                            type="radio"
                            class="radio-input"
                            name="technical-issues"
                            value="Ive had technical issues with FRISKY Premium"
                        >
                        <label
                            class="radio-label"
                            for="technical-issues"
                        >Ive had technical issues with FRISKY Premium</label>
                    </div>
                    <div class="radio-group">
                        <input
                            id="dont-use-enough"
                            v-model="selectedChoice"
                            type="radio"
                            class="radio-input"
                            name="dont-use-enough"
                            value="I dont use FRISKY Premium enough"
                        >
                        <label
                            class="radio-label"
                            for="dont-use-enough"
                        >I dont use FRISKY Premium enough</label>
                    </div>
                    <div class="radio-group">
                        <input
                            id="other"
                            v-model="selectedChoice"
                            type="radio"
                            class="radio-input"
                            name="other"
                            value="other"
                        >
                        <label
                            class="radio-label"
                            for="other"
                        >Other (Please specify below)</label>
                    </div>
                </div>
                <div
                    v-if="selectedChoice === 'other'"
                    class="text-feedback"
                >
                    <h3 class="feedback-header fw-bold">Please tell us more about why you are cancelling:</h3>
                    <textarea
                        v-model="textFeedback"
                        placeholder="Please specify"
                        class="text-feedback-input"
                    />
                </div>
                <div class="actions">
                    <BaseButton
                        class="action-button"
                        variant="secondary"
                        size="l"
                        type="submit"
                        :disabled="isLoading"
                        @click="() => {}"
                    ><span class="button-content">Send Feedback <BaseIcon
                        v-if="isLoading"
                        class="loading-icon spin"
                        name="spinner"
                    /></span></BaseButton>
                </div>
            </form>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

:root {
    --text-size: 14px;
}

.cancellation-feedback-container {
    display: flex;
    flex-direction: column;
    gap: $s-s;
}

.title {
    margin-top: $s-l;
}

.subtitle {
    font-size: var(--text-size);
    line-height: 150%;
}

.feedback-header {
    font-size: var(--text-size);
    line-height: 250%;
}

.radio-group {
    line-height: 250%;
}

.radio-input {
    margin-right: $s-m;
}

.radio-label {
    font-size: var(--text-size);
}

.actions {
    display: flex;
    align-items: center;
    justify-content: flex-start;

    .action-button {
        margin-top: $s-l;
    }
}

.button-content {
    display: flex;
    gap: $s-m;
    align-items: center;
}

.text-feedback-input {
    width: 732px;
    max-width: 100%;
    height: 185px;
    padding: $s-m;
    font-size: var(--text-size);
    line-height: 150%;
    color: $c-white;
    background-color: $c-surface-overlay;
    border: 1px solid $c-border-default;
    border-radius: $radius-5;

    &::placeholder {
        color: $c-text-faded;
    }
}

.loading-icon {
    max-width: 20px;
    max-height: 20px;
    color: $c-white;
}

@include tablet {
    :root {
        --text-size: 16px;
    }

    .cancellation-feedback-container {
        gap: $s-xl;
    }

    .title {
        margin-top: $s-m;
    }

    .actions {
        margin-top: $s-xxl;
    }
}
</style>
